import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { TransportSectionProps } from './types'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  ColoredBadge,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Icon } from '../../../atoms/Icon'

const DetailsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  svg {
    width: 12px;
    height: 12px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    svg {
      transform: translateX(2px);
    }
  }
`

export const TransportSection: React.FC<TransportSectionProps> = ({
  building,
  detailsLink,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')

  const getTransportQualityText = (
    quality: string | number | undefined
  ): string => {
    if (!quality) return '-'

    const qualityMap: Record<string, string> = {
      excellent: t('common.transport.excellent'),
      good: t('common.transport.good'),
      moderate: t('common.transport.moderate'),
      poor: t('common.transport.poor'),
      'very well served': t('common.transport.excellent'),
      'well served': t('common.transport.good'),
      'medium served': t('common.transport.moderate'),
      'badly served': t('common.transport.poor'),
      'very badly served': t('common.transport.poor'),
    }

    return qualityMap[quality.toString().toLowerCase()] || quality.toString()
  }

  const formatTravelTime = (time: number | string | undefined): string => {
    if (!time) return '-'

    if (typeof time === 'number') {
      if (time < 60) {
        return `${time} min`
      } else {
        const hours = Math.floor(time / 60)
        const minutes = time % 60
        return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
      }
    }

    const travelTimeMap: Record<string, string> = {
      short: tCommon('short'),
      medium: tCommon('medium'),
      long: tCommon('long'),
    }

    return travelTimeMap[time.toString().toLowerCase()] || time.toString()
  }

  const getNoiseLevel = (level: string | number | undefined): string => {
    if (!level) return '-'

    const noiseLevelMap: Record<string, string> = {
      'up to 45 db': t('common.noise.low'),
      '45 to 52 db': t('common.noise.moderate'),
      'more than 52 db': t('common.noise.high'),
    }

    return noiseLevelMap[level.toString().toLowerCase()] || level.toString()
  }

  const hasTransportInfo =
    building?.public_transport_quality ||
    building?.travel_time_to_centers ||
    building?.noise_exposure ||
    building?.distance_to_highvoltage_powerlines

  if (!hasTransportInfo) {
    return null
  }

  const getDistanceToHighVoltagePowerlines = (
    distance_to_highvoltage_powerlines: string | number | undefined
  ): string | undefined => {
    if (!distance_to_highvoltage_powerlines) return '-'

    const distanceToHighVoltagePowerlinesMap: Record<string, string> = {
      'over 100m': t('common.distance.over100m'),
      '0 up to 100m': t('common.distance.upTo100m'),
    }

    return (
      distanceToHighVoltagePowerlinesMap[
        distance_to_highvoltage_powerlines.toString().toLowerCase()
      ] || distance_to_highvoltage_powerlines.toString()
    )
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2l2-2h4l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.transport.title')}
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          {building?.public_transport_quality && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Qualite transport public')}
                explanation={tCommon(
                  'propertyExplanations.transport.publicTransportQuality'
                )}
              >
                {t('common.buildingTooltip.Qualite transport public')}
              </DataLabelWithTooltip>
              <ColoredBadge
                $type="public_transport"
                $value={building.public_transport_quality}
              >
                {getTransportQualityText(building.public_transport_quality)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.travel_time_to_centers && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Temps trajet centres')}
                explanation={tCommon(
                  'propertyExplanations.transport.travelTimeToCenters'
                )}
              >
                {t('common.buildingTooltip.Temps trajet centres')}
              </DataLabelWithTooltip>
              <ColoredBadge
                $type="travel_time"
                $value={building.travel_time_to_centers}
              >
                {formatTravelTime(building.travel_time_to_centers)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.noise_exposure && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('common.buildingTooltip.Exposition bruit')}
                explanation={tCommon(
                  'propertyExplanations.transport.noiseExposure'
                )}
              >
                {t('common.buildingTooltip.Exposition bruit')}
              </DataLabelWithTooltip>
              <ColoredBadge $type="noise" $value={building.noise_exposure}>
                {getNoiseLevel(building.noise_exposure)}
              </ColoredBadge>
            </DataRow>
          )}

          {building?.distance_to_highvoltage_powerlines && (
            <DataRow>
              <DataLabelWithTooltip
                label={t(
                  'common.buildingTooltip.Distance lignes haute tension'
                )}
                explanation={tCommon(
                  'propertyExplanations.transport.distanceToPowerlines'
                )}
              >
                {t('common.buildingTooltip.Distance lignes haute tension')}
              </DataLabelWithTooltip>
              <ColoredBadge
                $type="distance_to_highvoltage_powerlines"
                $value={building.distance_to_highvoltage_powerlines}
              >
                {getDistanceToHighVoltagePowerlines(
                  building.distance_to_highvoltage_powerlines
                )}
              </ColoredBadge>
            </DataRow>
          )}
        </DataGrid>
        {detailsLink && (
          <DetailsLink href={detailsLink}>
            <Icon name="arrow-right" size="small" />
            {t('propertyTooltip.transport.viewDetails')}
          </DetailsLink>
        )}
      </SectionContent>
    </InfoSection>
  )
}

export default TransportSection
